import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { PendenciaAPIService } from 'src/app/modules/pendencias/services/pendencia-api.service';
import { PendenciaToastComponent } from '../components/pendencia-toast/pendencia-toast.component';
import { PendenciaAvisoEvento } from '../interfaces/pendencia/pendencia.interface';
import { UsuarioService } from './usuario.service';
import { WebSocketService } from './websocket.service';

/** Chave de sessão da modal do login: maior id de pendência já exibido nela. */
export const CHAVE_SESSAO_MODAL_PENDENCIAS = 'pendenciasModalUltimoId';

/**
 * Avisos de pendência em tempo real: o contador do ícone do header (vem sempre do servidor, que
 * é a fonte da verdade da "novidade não vista") e o toast que só sai no clique.
 *
 * O header chama {@link iniciar} ao montar (depois de cada login). Telas abertas (lista,
 * visualização) assinam {@link getAtualizacoes} para se recarregar quando alguém mexe numa
 * pendência.
 */
@Injectable({
  providedIn: 'root'
})
export class PendenciaAvisoService {
  private contador$ = new BehaviorSubject<number>(0);
  private atualizacoes$ = new Subject<PendenciaAvisoEvento>();
  private assinatura: Subscription | null = null;
  private sessaoAtiva = false;

  constructor(
    private ws: WebSocketService,
    private api: PendenciaAPIService,
    private toastr: ToastrService,
    private router: Router,
    usuarioService: UsuarioService
  ) {
    // Logout (usuário logado vira vazio): zera o contador e fecha a conexão do hub. Escutar
    // aqui, e não chamar daqui de dentro do UsuarioService, evita amarrar o serviço de usuário ao
    // módulo de pendências.
    usuarioService.getUsuarioLogado().subscribe(usuario => {
      if (usuario) {
        this.sessaoAtiva = true;
      } else if (this.sessaoAtiva) {
        this.sessaoAtiva = false;
        this.encerrarSessao();
      }
    });
  }

  private encerrarSessao(): void {
    this.contador$.next(0);
    this.ws.desconectar();
  }

  iniciar(): void {
    this.ws.garantirConexaoDoTokenAtual();

    // Zera antes de buscar: depois de trocar de usuário, o número do anterior não pode aparecer.
    this.contador$.next(0);
    this.recarregarContador();

    if (!this.assinatura) {
      this.assinatura = this.ws.getPendencias().subscribe(aviso => this.aoReceber(aviso));
    }
  }

  getContador(): Observable<number> {
    return this.contador$.asObservable();
  }

  getAtualizacoes(): Observable<PendenciaAvisoEvento> {
    return this.atualizacoes$.asObservable();
  }

  recarregarContador(): void {
    if (!localStorage.getItem('token')) return;

    this.api.contador().subscribe({
      next: (c) => this.contador$.next(c?.comNovidade ?? 0),
      // Contador é acessório: sem ele o header continua funcionando.
      error: () => { }
    });
  }

  private aoReceber(aviso: PendenciaAvisoEvento): void {
    // Rede de segurança: sem sessão, nenhum aviso chega à tela (nem à de login).
    if (!localStorage.getItem('token')) return;

    this.recarregarContador();
    this.atualizacoes$.next(aviso);

    if (aviso.toast) {
      this.mostrarToast(aviso);
    }
  }

  private mostrarToast(aviso: PendenciaAvisoEvento): void {
    // Toast próprio (PendenciaToastComponent): no lugar do título vai a frase do acontecimento
    // ("Nova pendência de João") e, no da mensagem, o título da pendência em destaque. Sem
    // enableHtml: os dois são texto livre de usuário.
    const toast = this.toastr.show(aviso.titulo, aviso.mensagem, {
      toastComponent: PendenciaToastComponent,
      toastClass: 'ngx-toastr toast-pendencia',
      disableTimeOut: true,
      closeButton: true,
      tapToDismiss: true
    });

    toast?.onTap.pipe(take(1)).subscribe(() => {
      this.router.navigate(['/pendencias/minhas-pendencias/visualizar', aviso.pendenciaId]);
    });
  }
}
